/**
 * R2's shared medals and trophies block. One web implementation, fed
 * entirely by props, so a fan profile screen and account home mount the
 * identical component and cannot disagree on the two-number shape or the
 * scope switch.
 *
 * The segmented scope switch drives one shared loading, failed, or ready body.
 */
import {
  cn,
  HIT_SLOP,
  NEUTRAL_SURFACE_FRAME,
  Skeleton,
  SegmentedControl,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  awardChipAccessibilityLabel,
  awardChipClass,
  awardColorRole,
  chipsForScope,
  type typeAwardChip,
  type typeAwardScope,
} from "./awardChips";
import { medalLine, trophyLine, type typeAwardCounts } from "./awardCounts";

export type typeMedalsBlockProps = {
  isLoading: boolean;
  hasFailed: boolean;
  thisSeason: typeAwardCounts;
  allTime: typeAwardCounts;
  chips: readonly typeAwardChip[];
  title: string;
  thisSeasonLabel: string;
  allTimeLabel: string;
  tabsLabel: string;
  unavailableMessage: string;
  testId?: string | undefined;
  onChipSelect?: ((chip: typeAwardChip) => void) | undefined;
};

export const MedalsBlock = (props: typeMedalsBlockProps): ReactNode => {
  const [scope, setScope] = useState<typeAwardScope>("thisSeason");
  const counts = scope === "allTime" ? props.allTime : props.thisSeason;
  const chips = chipsForScope(props.chips, scope);

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4"
      data-testid={props.testId}
    >
      <h2 className="text-h2 text-ink">{props.title}</h2>

      <SegmentedControl
        label={props.tabsLabel}
        value={scope}
        fullWidth
        options={[
          { value: "thisSeason", label: props.thisSeasonLabel },
          { value: "allTime", label: props.allTimeLabel },
        ]}
        onValueChange={(value) => {
          setScope(value as typeAwardScope);
        }}
      />

      {props.isLoading ? (
        <div className="flex flex-col gap-2" data-testid="medals-loading">
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      ) : props.hasFailed ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="medals-unavailable"
        >
          {props.unavailableMessage}
        </p>
      ) : (
        <>
          <p className="text-body text-ink" data-testid="medals-line">
            {medalLine(counts)}
          </p>
          <p className="text-body text-ink" data-testid="trophies-line">
            {trophyLine(counts)}
          </p>
          <ul className="flex flex-wrap gap-2" data-testid="award-chips">
            {chips.map((chip) => (
              <li
                key={chip.awardGrantId}
                className={cn(
                  "flex flex-col gap-1 rounded-sm px-3 py-2",
                  awardChipClass(awardColorRole(chip.color)),
                  NEUTRAL_SURFACE_FRAME,
                )}
              >
                {props.onChipSelect === undefined ? (
                  <>
                    <span className="text-body-strong">{chip.name}</span>
                    {chip.contestName !== null ? (
                      <span className="text-caption text-ink-secondary">
                        {chip.contestName}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      props.onChipSelect?.(chip);
                    }}
                    aria-label={awardChipAccessibilityLabel(chip)}
                    data-testid={`award-chip-${chip.awardGrantId}`}
                    // The chip's own text is shorter than the 44px tap-target
                    // floor, so this keeps the visual chip height and pads the
                    // real hit area with the same hit-slop overlay the compact
                    // `Button` size uses (packages/ui/src/lib/focus.ts).
                    className={cn(
                      "relative flex flex-col gap-1 text-left",
                      HIT_SLOP,
                    )}
                  >
                    <span className="text-body-strong">{chip.name}</span>
                    {chip.contestName !== null ? (
                      <span className="text-caption text-ink-secondary">
                        {chip.contestName}
                      </span>
                    ) : null}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
